import { useCallback } from 'react';
import { Flex, Badge, Card, Tooltip } from '@scale/scaleui-radix';
import { AuditRubricCriterion } from '@scale/shared-types';
import {
  AttemptAuditCriterionScore,
  DEFAULT_ATTEMPT_AUDIT_SCORE,
} from '@scale/shared/types/speedAudit';
import { ChatResponseV2, ThreadedChatResponse } from '@scale/shared/types/chat';
import { TaskType } from '@scale/shared/defaults';
import { ViewCriterionName } from './criterionComponents/CriterionName';
import { FillCriterionSteps } from './criterionComponents/CriterionSteps';
import { FillCriterionAnswerOptions } from './criterionComponents/CriterionAnswerOptions';
import { FillCriterionErrorCategories } from './criterionComponents/CriterionErrorCategories';
import { FillCriterionJustification } from './criterionComponents/CriterionJustification';

interface CriterionToFillProps {
  projectId: string;
  criterion: AuditRubricCriterion;
  response: {
    type: TaskType;
    response: ThreadedChatResponse | ChatResponseV2;
  };
  criterionAnswer: AttemptAuditCriterionScore;
  onCriterionAnswerChange: (answer: AttemptAuditCriterionScore) => void;
  isAnswered: boolean;
  isComplete: boolean;
}

export const CriterionToFill = ({
  projectId,
  criterion,
  response,
  criterionAnswer,
  onCriterionAnswerChange,
  isAnswered,
  isComplete,
}: CriterionToFillProps) => {
  const setSelectedOption = useCallback(
    (rawValue?: string) => {
      onCriterionAnswerChange({
        ...criterionAnswer,
        rawValue,
        rawScore: criterion.answerOptions.find(option => option.text === rawValue)?.score,
      });
    },
    [criterion.answerOptions, criterionAnswer, onCriterionAnswerChange],
  );

  const setSelectedErrorCategory = useCallback(
    (errorCategory?: string) => {
      onCriterionAnswerChange({
        ...criterionAnswer,
        errorCategory,
      });
    },
    [criterionAnswer, onCriterionAnswerChange],
  );

  const setFeedback = useCallback(
    (justification: string) => {
      onCriterionAnswerChange({
        ...criterionAnswer,
        justification,
      });
    },
    [criterionAnswer, onCriterionAnswerChange],
  );

  return (
    <div style={{ minWidth: 200 }}>
      <Card>
        <Flex direction="column" gap="4" width="100%">
          <ViewCriterionName
            criterion={criterion}
            renderedExtraInfo={
              !isComplete ? (
                <Tooltip
                  content={
                    criterion.required
                      ? `This criterion required.`
                      : `If you choose to score this criterion, please provide feedback if required.`
                  }
                >
                  <Badge color="error">Incomplete</Badge>
                </Tooltip>
              ) : !isAnswered ? (
                <Tooltip
                  content={`This criterion is not scored. It will be marked as a score ${DEFAULT_ATTEMPT_AUDIT_SCORE} unless otherwise specified.`}
                >
                  <Badge color="warning">Default Score</Badge>
                </Tooltip>
              ) : null
            }
            showQuestionText={false}
          />
          <FillCriterionSteps criterion={criterion} response={response} />
          <div className="h-1" />
          <FillCriterionAnswerOptions
            criterion={criterion}
            selectedOption={criterionAnswer.rawValue}
            setSelectedOption={setSelectedOption}
          />
          <FillCriterionErrorCategories
            projectId={projectId}
            errorCategories={criterion.errorCategories}
            selectedErrorCategory={criterionAnswer.errorCategory}
            setSelectedErrorCategory={setSelectedErrorCategory}
          />
          <FillCriterionJustification
            feedback={criterionAnswer.justification ?? ''}
            setFeedback={setFeedback}
            required={
              criterion.answerOptions.find(option => option.text === criterionAnswer.rawValue)
                ?.requiresJustification ?? false
            }
            disabled={criterionAnswer.rawValue === undefined}
          />
        </Flex>
      </Card>
    </div>
  );
};
